import { useEffect, useState } from 'react';
import { useGameStore } from '../store/gameStore';

// ─── The moment before the match ────────────────────────────────────
//
// This screen exists because the game used to drop a player straight into
// round one the instant a seat filled, sometimes against nobody at all. It is
// the visible half of the handshake: it appears once both clients have
// confirmed each other's presence, and it does not move until both players
// have pressed READY. Only then does the host publish the shared instant and
// the countdown run, on both machines, from the same moment.
//
// An earlier version counted down automatically. It played as a match that
// starts itself: technically synchronised, humanly unagreed.

const VIOLET = '#3E7BFA';
const TEAL = '#14B8A6';

/** Two letters is enough to read at a glance and never overflows the disc. */
function initials(name: string | null): string {
  const clean = (name ?? '').trim();
  if (!clean) return '??';
  const parts = clean.split(/\s+/);
  if (parts.length > 1) return (parts[0][0] + parts[1][0]).toUpperCase();
  return clean.slice(0, 2).toUpperCase();
}

function Fighter({ name, accent, side, ready }: {
  name: string; accent: string; side: 'left' | 'right'; ready: boolean;
}) {
  return (
    <div
      style={{
        flex: 1, minWidth: 0,
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12,
        // The two sides slide in from their own edge, which reads as opposition
        // rather than as two things appearing.
        animation: `versus-enter-${side} 420ms cubic-bezier(0.22, 1, 0.36, 1) both`,
      }}
    >
      <div
        style={{
          width: 76, height: 76, borderRadius: '50%',
          background: `${accent}18`, border: `2px solid ${accent}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontFamily: 'var(--font-heading)', fontWeight: 900, fontSize: '1.5rem',
          color: accent, letterSpacing: '0.02em',
          boxShadow: `0 0 28px ${accent}33`,
        }}
      >
        {initials(name)}
      </div>
      <span
        style={{
          fontFamily: 'var(--font-heading)', fontWeight: 800,
          fontSize: '0.8rem', letterSpacing: '0.1em', color: '#eee',
          maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}
      >
        {name.toUpperCase()}
      </span>
      {/* Both states are printed rather than only the good one, so an absence
          is legible: a blank space under one name would read as broken. */}
      <span
        style={{
          fontFamily: 'var(--font-sans)', fontWeight: 700, fontSize: '0.85rem', letterSpacing: '0.12em',
          color: ready ? accent : '#4a4a52',
          transition: 'color 200ms ease',
        }}
      >
        {ready ? 'READY' : 'WAITING'}
      </span>
    </div>
  );
}

export function Versus() {
  const nickname = useGameStore((s) => s.nickname);
  const opponentNickname = useGameStore((s) => s.opponentNickname);
  const localReady = useGameStore((s) => s.localReady);
  const opponentReady = useGameStore((s) => s.opponentReady);
  const declareReady = useGameStore((s) => s.declareReady);
  const matchStartAt = useGameStore((s) => s.matchStartAt);
  const totalRounds = useGameStore((s) => s.totalRounds);
  const selectedRealm = useGameStore((s) => s.selectedRealm);
  const resetGame = useGameStore((s) => s.resetGame);
  const setView = useGameStore((s) => s.setView);

  // Counts down to the shared instant rather than from a fixed number, so both
  // screens land on zero together even if one of them opened late.
  const [remaining, setRemaining] = useState(() =>
    matchStartAt ? Math.max(0, matchStartAt - Date.now()) : 0);

  useEffect(() => {
    if (!matchStartAt) return;
    const tick = () => setRemaining(Math.max(0, matchStartAt - Date.now()));
    tick();
    const id = setInterval(tick, 80);
    return () => clearInterval(id);
  }, [matchStartAt]);

  const seconds = Math.ceil(remaining / 1000);

  function handleLeave() {
    resetGame();
    setView('home');
  }

  return (
    <div
      style={{
        minHeight: '100dvh', display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center', gap: 34, padding: '0 22px',
      }}
    >
      <style>{`
        @keyframes versus-enter-left  { from { opacity: 0; transform: translateX(-26px); } to { opacity: 1; transform: none; } }
        @keyframes versus-enter-right { from { opacity: 0; transform: translateX( 26px); } to { opacity: 1; transform: none; } }
        @keyframes versus-pulse       { 0%, 100% { transform: scale(1); opacity: 1; } 50% { transform: scale(1.07); opacity: 0.82; } }
        @media (prefers-reduced-motion: reduce) {
          [data-versus] *, [data-versus] { animation: none !important; }
        }
      `}</style>

      <div data-versus style={{ width: '100%', maxWidth: 420, display: 'flex', flexDirection: 'column', gap: 34 }}>

        <div style={{ textAlign: 'center' }}>
          <div style={{
            fontFamily: 'var(--font-mono)', fontSize: '0.8rem',
            letterSpacing: '0.3em', color: '#b6b6be',
          }}>
            MATCH FOUND
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Fighter name={nickname || 'YOU'} accent={TEAL} side="left"
                   ready={localReady || matchStartAt !== null} />
          <span
            style={{
              fontFamily: 'var(--font-heading)', fontStyle: 'italic', fontWeight: 900,
              fontSize: '1.5rem', color: VIOLET, letterSpacing: '-0.02em',
              animation: 'versus-pulse 1.1s ease-in-out infinite',
              flexShrink: 0,
            }}
          >
            VS
          </span>
          <Fighter name={opponentNickname || 'OPPONENT'} accent={VIOLET} side="right"
                   ready={opponentReady || matchStartAt !== null} />
        </div>

        {/* What they are about to play, so the versus screen carries information
            and not only atmosphere. */}
        <div style={{
          display: 'flex', justifyContent: 'center', gap: 18,
          fontFamily: 'var(--font-mono)', fontSize: '0.8rem',
          letterSpacing: '0.14em', color: '#b6b6be',
        }}>
          <span>{selectedRealm === 'ALL' ? 'ALL REALMS' : selectedRealm.replace(/_/g, ' ')}</span>
          <span style={{ color: '#3E4E70' }}>|</span>
          <span>{totalRounds} QUESTIONS</span>
        </div>

        {/* Fixed height across all three states, so the layout does not jump
            when the button becomes a wait message becomes a number. */}
        <div
          style={{
            minHeight: 84, display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', gap: 10,
          }}
        >
          {matchStartAt !== null ? (
            <div
              key={seconds}
              style={{
                fontFamily: 'var(--font-heading)', fontWeight: 900,
                fontSize: '2.6rem', color: '#fff', lineHeight: 1,
                animation: 'versus-pulse 300ms ease-out',
              }}
            >
              {seconds > 0 ? seconds : 'GO'}
            </div>
          ) : localReady ? (
            <span
              style={{
                fontFamily: 'var(--font-mono)', fontSize: '0.8rem',
                letterSpacing: '0.22em', color: '#b6b6be',
                animation: 'versus-pulse 1.4s ease-in-out infinite',
              }}
            >
              WAITING FOR OPPONENT
            </span>
          ) : (
            <button
              onClick={declareReady}
              style={{
                background: `${TEAL}14`, border: `2px solid ${TEAL}`, color: TEAL,
                borderRadius: 999, padding: '13px 38px', cursor: 'pointer',
                fontFamily: 'var(--font-heading)', fontWeight: 900,
                fontSize: '0.95rem', letterSpacing: '0.12em',
                boxShadow: `0 0 24px ${TEAL}22`,
              }}
            >
              I'M READY
            </button>
          )}
        </div>

        <button
          onClick={handleLeave}
          style={{
            alignSelf: 'center', background: 'none', border: 'none',
            color: '#8E8EA2', cursor: 'pointer', minHeight: 44, padding: '10px 22px',
            fontFamily: 'var(--font-mono)', fontSize: '0.85rem', letterSpacing: '0.18em',
          }}
        >
          LEAVE
        </button>
      </div>
    </div>
  );
}
